import { AxiosInstance } from 'axios'
import { Result } from '../../../Shared/Protocol/protocol-result'
import {
  IHCPArteKanbanColunaPayload,
  IHCPArteKanbanColunaResponse,
  IHCPArteKanbanPayload,
  IHCPArteKanbanResponse,
  ICreativeFlowSearchPayload,
  IBriefing,
  IArt,
  IDesignerMetric,
  IHCPArteDesigner,
  ITag,
} from './creative-flow.interface'

export class CreativeFlowService {
  constructor(private readonly api: AxiosInstance) {}

  public async getKanban(
    payload?: IHCPArteKanbanPayload
  ): Promise<Result<IHCPArteKanbanResponse>> {
    try {
      const response = await this.api.get<IHCPArteKanbanResponse>(
        '/customization-flow/kanban',
        { params: payload }
      )
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async getKanbanColumn(
    payload: IHCPArteKanbanColunaPayload
  ): Promise<Result<IHCPArteKanbanColunaResponse>> {
    try {
      const response = await this.api.get<IHCPArteKanbanColunaResponse>(
        '/customization-flow/kanban/column',
        { params: payload }
      )
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async searchFlows(
    payload: ICreativeFlowSearchPayload
  ): Promise<Result<any>> {
    try {
      const response = await this.api.get('/customization-flow/flows/search', {
        params: payload,
      })
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async getReplicableArts(productId: number): Promise<Result<IArt[]>> {
    try {
      const response = await this.api.get(
        `/customization-flow/flows/${productId}/replicable-arts`
      )
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async listBriefings(): Promise<Result<IBriefing[]>> {
    try {
      const response = await this.api.get('/customization-flow/briefings')
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async getBriefing(id: number): Promise<Result<IBriefing>> {
    try {
      const response = await this.api.get(`/customization-flow/briefings/${id}`)
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async getBriefingByProduct(productId: number): Promise<Result<IBriefing>> {
    try {
      const response = await this.api.get(
        `/customization-flow/briefings/product/${productId}`
      )
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async getBriefingByOrder(orderId: string): Promise<Result<IBriefing[]>> {
    try {
      const response = await this.api.get(
        `/customization-flow/briefings/order/${orderId}`
      )
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async listArts(): Promise<Result<IArt[]>> {
    try {
      const response = await this.api.get('/customization-flow/arts')
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async getArt(id: number): Promise<Result<IArt>> {
    try {
      const response = await this.api.get(`/customization-flow/arts/${id}`)
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async getArtRevisions(): Promise<Result<any[]>> {
    try {
      const response = await this.api.get('/customization-flow/art-revisions')
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async getDesignerDashboard(): Promise<Result<IDesignerMetric[]>> {
    try {
      const response = await this.api.get('/customization-flow/dashboard/designers')
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public getNotificationsStreamUrl(): string {
    // SSE usually needs the full URL, using the base URL from api config if available
    const baseURL = this.api.defaults.baseURL || ''
    return `${baseURL}/customization-flow/notifications-stream`
  }

  public async listDesigners(): Promise<Result<IHCPArteDesigner[]>> {
    try {
      const response = await this.api.get('/customization-flow/designers-list')
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async getFlowFilterData(): Promise<Result<any>> {
    try {
      const response = await this.api.get('/customization-flow/flow-filter-data')
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async getNotifications(): Promise<Result<any[]>> {
    try {
      const response = await this.api.get('/customization-flow/notifications')
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }

  public async listTags(): Promise<Result<ITag[]>> {
    try {
      const response = await this.api.get('/customization-flow/tags')
      return Result.ok(response.data)
    } catch (error: any) {
      return Result.fail(error?.response?.data ?? error)
    }
  }
}
